import {
  AlertCircle,
  AlertTriangle,
  Bell,
  CheckCircle2,
  CreditCard,
  Info,
  PlugZap,
  ShieldCheck,
  Upload,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { notificationPath } from './notification-routing';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';

import type { AppNotification, NotificationType } from './types';

interface NotificationCenterProps {
  notifications: AppNotification[];
  unreadCount: number;
  hasMore: boolean;
  filter: 'all' | 'unread';
  onFilterChange: (filter: 'all' | 'unread') => void;
  onLoadMore: () => Promise<void>;
  onMarkRead: (id: string) => Promise<void>;
  onMarkAllRead: () => Promise<void>;
  onClose: () => void;
}

// Icon configs use design tokens so they invert correctly in dark mode
const ICONS: Record<NotificationType, { icon: LucideIcon; className: string }> = {
  CAMPAIGN_COMPLETED:          { icon: CheckCircle2, className: 'text-success-default bg-success-muted' },
  CAMPAIGN_FAILED:             { icon: AlertCircle,  className: 'text-danger-default bg-danger-muted'   },
  AUTOMATION_ERROR:            { icon: AlertTriangle,className: 'text-warning-default bg-warning-muted' },
  AUTOMATION_ENROLLMENT_SPIKE: { icon: AlertTriangle,className: 'text-warning-default bg-warning-muted' },
  IMPORT_COMPLETED:            { icon: Upload,       className: 'text-info-default bg-info-muted'       },
  IMPORT_FAILED:               { icon: AlertCircle,  className: 'text-danger-default bg-danger-muted'   },
  DOMAIN_VERIFIED:             { icon: ShieldCheck,  className: 'text-success-default bg-success-muted' },
  DOMAIN_FAILED:               { icon: AlertCircle,  className: 'text-danger-default bg-danger-muted'   },
  PAYMENT_FAILED:              { icon: CreditCard,   className: 'text-danger-default bg-danger-muted'   },
  PLAN_LIMIT_APPROACHING:      { icon: Zap,          className: 'text-warning-default bg-warning-muted' },
  CONNECTOR_ERROR:             { icon: PlugZap,      className: 'text-warning-default bg-warning-muted' },
  CONNECTOR_DISCONNECTED:      { icon: PlugZap,      className: 'text-warning-default bg-warning-muted' },
  SEGMENT_EVALUATED:           { icon: CheckCircle2, className: 'text-info-default bg-info-muted'       },
  SYSTEM_ALERT:                { icon: Info,         className: 'text-text-secondary bg-bg-muted'       },
};

export function NotificationCenter({
  notifications,
  unreadCount,
  hasMore,
  filter,
  onFilterChange,
  onLoadMore,
  onMarkRead,
  onMarkAllRead,
  onClose,
}: NotificationCenterProps): React.JSX.Element {
  const navigate = useNavigate();
  const panelRef = React.useRef<HTMLDivElement>(null);
  const visible = filter === 'unread' ? notifications.filter((item) => !item.read) : notifications;

  // Return focus to bell button on close
  React.useEffect(() => {
    return () => {
      (document.querySelector('[aria-label^="Notifications"]') as HTMLElement | null)?.focus();
    };
  }, []);

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-label="Notifications panel"
      className="absolute right-0 top-12 z-50 w-[calc(100vw-2rem)] max-w-[380px] overflow-hidden rounded-xl border border-border-subtle bg-surface-overlay shadow-xl"
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border-subtle px-4 py-3">
        <div>
          <p className="text-sm font-semibold text-text-primary">Notifications</p>
          <p className="text-xs text-text-tertiary" aria-live="polite" aria-atomic="true">
            {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void onMarkAllRead()}
          disabled={unreadCount === 0}
        >
          Mark all read
        </Button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 border-b border-border-subtle p-2" role="tablist" aria-label="Notification filter">
        {(['all', 'unread'] as const).map((item) => (
          <button
            key={item}
            type="button"
            role="tab"
            aria-selected={filter === item}
            onClick={() => onFilterChange(item)}
            className={cn(
              'h-8 flex-1 rounded-md text-sm font-medium capitalize transition-colors',
              'focus-visible:outline-2 focus-visible:outline-brand-default focus-visible:outline-offset-2',
              filter === item
                ? 'bg-text-primary text-text-inverse'
                : 'text-text-secondary hover:bg-bg-subtle',
            )}
          >
            {item === 'all' ? 'All' : 'Unread'}
          </button>
        ))}
      </div>

      {/* List */}
      <div
        className="max-h-[480px] overflow-y-auto"
        role="tabpanel"
        aria-label={filter === 'unread' ? 'Unread notifications' : 'All notifications'}
      >
        {visible.length === 0 ? (
          <div
            className="flex flex-col items-center gap-3 px-4 py-12 text-center"
            role="status"
            aria-live="polite"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-bg-muted">
              <Bell className="h-5 w-5 text-text-tertiary" aria-hidden="true" />
            </div>
            <div>
              <p className="text-sm font-medium text-text-primary">You're all caught up!</p>
              <p className="mt-0.5 text-xs text-text-tertiary">No new notifications</p>
            </div>
          </div>
        ) : (
          visible.map((notification) => {
            const config = ICONS[notification.type];
            const Icon = config.icon;
            return (
              <button
                key={notification.id}
                type="button"
                onClick={() => {
                  void onMarkRead(notification.id);
                  navigate(notificationPath(notification));
                  onClose();
                }}
                className={cn(
                  'grid w-full grid-cols-[2.5rem_1fr] gap-3 border-b border-border-subtle px-4 py-3 text-left',
                  'hover:bg-bg-subtle transition-colors',
                  'focus-visible:outline-2 focus-visible:outline-brand-default focus-visible:outline-offset-[-2px]',
                  !notification.read && 'border-l-[3px] border-l-brand-default pl-[13px]',
                )}
              >
                <span
                  className={cn(
                    'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                    config.className,
                  )}
                  aria-hidden="true"
                >
                  <Icon className="h-4 w-4" />
                </span>
                <span className="min-w-0">
                  <span
                    className={cn(
                      'block truncate text-sm text-text-primary',
                      !notification.read && 'font-semibold',
                    )}
                  >
                    {notification.title}
                  </span>
                  <span className="mt-0.5 block line-clamp-2 text-sm leading-5 text-text-secondary">
                    {notification.message}
                  </span>
                  <span className="mt-1 block text-xs text-text-tertiary">
                    {relativeTime(notification.createdAt)}
                  </span>
                </span>
              </button>
            );
          })
        )}

        {hasMore && (
          <div className="p-3">
            <Button variant="secondary" size="sm" className="w-full" onClick={() => void onLoadMore()}>
              Load more
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function relativeTime(value: string): string {
  const diffMs = Date.now() - new Date(value).getTime();
  const minutes = Math.max(0, Math.round(diffMs / 60_000));
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  if (hours < 48) return 'Yesterday';
  const days = Math.round(hours / 24);
  return `${days} days ago`;
}
